import { useEffect } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useAuth } from "../../lib/auth-context";
import { usePro } from "../../lib/pro-context";
import { LoadingSpinner } from "./LoadingSpinner";

type OnboardingGuardProps = {
	children: React.ReactNode;
};

/**
 * OnboardingGuard wraps protected pro routes.
 * It redirects users to /onboarding if they haven't completed onboarding.
 * This guard should be used AFTER AuthGuard in the component hierarchy.
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
	const { isAuthenticated, isLoading: authLoading, hasProAccess } = useAuth();
	const { onboardingRequired, pendingInvitationToken, isLoading: proLoading } = usePro();
	const navigate = useNavigate();
	const routerState = useRouterState();
	const currentPath = routerState.location.pathname;

	const isLoading = authLoading || proLoading;

	useEffect(() => {
		// Redirect to accept invitation if user has a pending team invitation
		if (!isLoading && isAuthenticated && pendingInvitationToken) {
			navigate({ to: "/accept-invitation", search: { token: pendingInvitationToken } });
			return;
		}

		// Only redirect if we have pro access and onboarding is required.
		// Admin-only users don't need onboarding.
		// The `!currentPath.startsWith("/onboarding")` check is the real loop guard:
		// it prevents re-redirect on remount when the user is already on /onboarding.
		if (
			!isLoading &&
			isAuthenticated &&
			hasProAccess &&
			onboardingRequired &&
			!currentPath.startsWith("/onboarding")
		) {
			navigate({ to: "/onboarding" });
		}
	}, [
		isLoading,
		isAuthenticated,
		hasProAccess,
		onboardingRequired,
		pendingInvitationToken,
		currentPath,
		navigate,
	]);

	if (isLoading) {
		return <LoadingSpinner />;
	}

	// Show spinner while redirecting to accept invitation
	if (pendingInvitationToken) {
		return <LoadingSpinner />;
	}

	// Show spinner while redirecting to onboarding
	if (hasProAccess && onboardingRequired && !currentPath.startsWith("/onboarding")) {
		return <LoadingSpinner />;
	}

	return <>{children}</>;
}
